import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { AuthTypes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import Lnk from 'src/navigation/Lnk'
import { organizationInfoUrl, profileInfoUrl } from 'src/navigation/urls'
import {
  deleteCognitoSignout,
  deleteCspEmulatedSessions,
  deleteSignout,
} from 'src/redux/actions/auth.actions'
import {
  selectAuthTypeFromState,
  selectIsCspAuthenticatedFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import variables from 'src/style/variables.json'
import { css } from 'styled-components'

interface ProfilePopupMenuProps {
  user: UserListModel | undefined
  userOrganization: OrganizationsListModel | undefined
}

const ProfilePopupMenu: React.FC<ProfilePopupMenuProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const authType = useSelector(selectAuthTypeFromState)

  const dispatch = useBackgroundDispatch()

  const handleOrgInfoAnalytics = (): void => {
    analytics?.track('avatar drop down - view org settings - text link press', {
      screen: '/home',
      interaction: 'text link press',
      intent: 'open org settings',
      user: props.user?.id,
    })
  }

  const logout = (): void => {
    if (isCspAuthenticated) {
      dispatch(deleteCspEmulatedSessions(false, false, analytics))
    } else {
      if (authType === AuthTypes.auth0) {
        dispatch(
          deleteSignout(
            false,
            analytics,
            false,
            calculatedBackgroundLocation.pathname,
          ),
        )
      } else {
        dispatch(deleteCognitoSignout(false, analytics))
      }
    }
  }

  return (
    <>
      <Lnk
        id="logoProfSet"
        css={css`
          ${getFontCss(
            12,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
        `}
        to={profileInfoUrl()}
        ariaLabelSource={'Profile'}
        tabIndex={0}
      >
        <div
          id="logoProfSet2"
          css={css`
            padding: ${variables.units['base-unit-2']}px
              ${variables.units['base-unit-5']}px;
            line-height: ${variables.units['base-unit-6']}px;
            width: 100%;
          `}
        >
          {translate('Profile Settings')}
        </div>
      </Lnk>
      {props.user &&
        props.userOrganization &&
        props.user?.hasOrgBookkeeperPermissions && (
          <Lnk
            to={organizationInfoUrl(props.userOrganization.id)}
            ariaLabelSource={'Organization Settings'}
            onClick={handleOrgInfoAnalytics}
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
            `}
            tabIndex={0}
          >
            <div
              css={css`
                padding: ${variables.units['base-unit-2']}px
                  ${variables.units['base-unit-5']}px;
                line-height: ${variables.units['base-unit-6']}px;
              `}
            >
              {translate('Organization Settings')}
            </div>
          </Lnk>
        )}
      <div
        css={css`
          background-color: ${styleConstants.colors.gray_lightest};
          border-top: 1px solid ${styleConstants.colors.gray_light};
          padding: ${variables.units['base-unit-2']}px
            ${variables.units['base-unit-5']}px;
          width: 100%;
          cursor: pointer;
          ${getFontCss(
            12,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
        `}
        onClick={logout}
        onKeyPress={logout}
        role="button"
        tabIndex={0}
      >
        <span
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.red,
              styleConstants.fontFamilies.medium,
            )};
            line-height: ${variables.units['base-unit-6']}px;
          `}
        >
          {translate('Log Out')}
        </span>
      </div>
    </>
  )
}

export default ProfilePopupMenu
